import {
  computeLongestStreak,
  GenericTask,
  StreakTask,
  TaskType,
} from '@playback-rewards/shared-libraries'
import { DateTime } from 'luxon'

export type StreakTaskWithProgress = StreakTask & { progress: number }

const MAX_VISIBLE_DAYS = 10
const WINDOW_SIZE = 3

// Visible flames for one day, per the "Streaks: Dynamic Zoom View" spec (Confluence).
// Priority: current day, goal and milestones; a 3-day window inside the current block
// (whole final block once inside it); Day 1 if there is room; then the days just before today.
const getDayView = (day: number, milestones: number[], includeDayOne: boolean) => {
  const goal = milestones[milestones.length - 1]
  const visible = new Set([day, ...milestones])
  const boundaries = [1, ...milestones]
  const blockStart = Math.max(...boundaries.filter((boundary) => boundary <= day)) + 1
  const blockEnd = (boundaries.find((boundary) => boundary > day) ?? blockStart) - 1

  if (blockEnd >= blockStart) {
    const isInsideFinalBlock = blockEnd === goal - 1 && day >= blockStart
    const fitsWholeBlock = blockEnd - blockStart + 1 <= MAX_VISIBLE_DAYS - visible.size + 1
    const windowStart =
      isInsideFinalBlock && fitsWholeBlock
        ? blockStart
        : Math.max(blockStart, Math.min(day - 1, blockEnd - WINDOW_SIZE + 1))
    const windowEnd =
      isInsideFinalBlock && fitsWholeBlock
        ? blockEnd
        : Math.min(windowStart + WINDOW_SIZE - 1, blockEnd)
    for (let d = windowStart; d <= windowEnd && visible.size < MAX_VISIBLE_DAYS; d++) visible.add(d)
  }

  const hasRoomForDayOne = visible.has(1) || visible.size < MAX_VISIBLE_DAYS
  if (includeDayOne && visible.size < MAX_VISIBLE_DAYS) visible.add(1)
  for (let d = day - 1; d > 1 && visible.size < MAX_VISIBLE_DAYS; d--) visible.add(d)

  return { days: Array.from(visible).sort((a, b) => a - b), hasRoomForDayOne }
}

// ponytail: goal + milestones alone can exceed 10 flames (spec: ~45+ days); spec leaves that open.
export const getVisibleStreakDays = (currentDay: number, milestoneDays: number[]): number[] => {
  const milestones = Array.from(new Set(milestoneDays)).sort((a, b) => a - b)
  if (!milestones.length) return []

  const goal = milestones[milestones.length - 1]
  if (goal <= MAX_VISIBLE_DAYS) return Array.from({ length: goal }, (_, index) => index + 1)

  const day = Math.min(Math.max(currentDay, 1), goal)
  // Day 1 never comes back once dropped, so replay the days before today.
  let includeDayOne = true
  for (let previousDay = 1; previousDay < day; previousDay++) {
    includeDayOne =
      includeDayOne && getDayView(previousDay, milestones, includeDayOne).hasRoomForDayOne
  }
  return getDayView(day, milestones, includeDayOne).days
}

export const getLatestStreakProgress = (events: string[] = []): number => {
  const sortedDates = [...events].sort()
  let start = sortedDates.length - 1

  while (
    start > 0 &&
    Date.parse(sortedDates[start]) - Date.parse(sortedDates[start - 1]) === 24 * 60 * 60 * 1000
  ) {
    start--
  }

  return computeLongestStreak(sortedDates.slice(start))
}

export const getActiveStreakProgress = (
  events: string[] = [],
  userTimezone = 'America/Chicago',
  now: DateTime = DateTime.now()
): number => {
  const sortedDates = [...events].sort()
  const latestDate = sortedDates[sortedDates.length - 1]
  const today = now.setZone(userTimezone)

  if (latestDate !== today.toISODate() && latestDate !== today.minus({ days: 1 }).toISODate()) {
    return 0
  }

  return getLatestStreakProgress(sortedDates)
}

export const getRetentionStreakTasksWithProgress = (
  tasks: GenericTask[] = []
): StreakTaskWithProgress[] =>
  tasks
    .filter(
      (task): task is StreakTask =>
        task.taskType === TaskType.STREAK && 'streakType' in task && task.streakType === 'RETENTION'
    )
    .map((task) => ({
      ...task,
      progress: getActiveStreakProgress(task.streak?.events, task.streak?.userTimezone),
    }))
